import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchRehabPlan } from "../../../store/slices/individualisedRehabPlanSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function IndividualRehabPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentRehabPlan, isLoading, error } = useSelector((state) => state.individualisedRehabPlan || {});

  useEffect(() => {
    if (id) dispatch(fetchRehabPlan(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentRehabPlan) {
      const timer = setTimeout(() => window.print(), 1000);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentRehabPlan, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading Rehab Plan...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentRehabPlan) return <div className="p-8 text-center">No record found.</div>;

  const d = currentRehabPlan;

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    }) : "-";

  return (
    <>
      <style jsx global>{`
        * { box-sizing: border-box !important; }
        body { font-family: 'Helvetica', Arial, sans-serif !important; background: #fff !important; color: #000 !important; font-size: 12px !important; line-height: 1.5 !important; margin: 0 !important; padding: 0 !important; }
        .print-container { padding: 30px !important; position: relative !important; }
        ${printFooterStyles}
        
        .logo { position: absolute !important; top: 15px !important; left: 20px !important; }
        .logo img { height: 60px !important; width: auto !important; }

        .header { text-align: center !important; margin-bottom: 25px !important; border-bottom: 2.5px solid #000 !important; padding-bottom: 10px !important; }
        .header h3 { font-size: 1.7rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
        .header h6 { font-size: 1.1rem !important; letter-spacing: 1px !important; text-transform: uppercase !important; }

        .meta-info { display: flex !important; justify-content: space-between !important; margin-bottom: 20px !important; padding: 10px !important; border: 1px solid #000 !important; background: #f9f9f9 !important; }
        .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; padding: 0 5px !important; min-width: 150px !important; display: inline-block !important; }

        .domain-section { margin-bottom: 20px !important; }
        .domain-grid { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; gap: 10px !important; margin-top: 10px !important; }
        .checkbox-item { display: flex !important; align-items: center !important; gap: 10px !important; font-weight: bold !important; }
        .box { width: 18px !important; height: 18px !important; border: 1.5px solid #000 !important; text-align: center !important; line-height: 15px !important; }
        .ticked { background: #000 !important; color: #fff !important; }

        .list-section { margin-top: 20px !important; }
        .section-label { background: #eee !important; padding: 5px 10px !important; font-weight: bold !important; border: 1px solid #000 !important; display: block !important; margin-bottom: 5px !important; }
        .text-block { border: 1px solid #000 !important; padding: 10px !important; min-height: 60px !important; margin-bottom: 15px !important; }
        .status-tag { font-size: 9px !important; background: #ddd !important; padding: 1px 4px !important; border-radius: 3px !important; margin-left: 10px !important; color: #444 !important; text-transform: uppercase !important; }

        .rehab-table { width: 100% !important; border-collapse: collapse !important; margin-top: 15px !important; }
        .rehab-table th, .rehab-table td { border: 1px solid #000 !important; padding: 8px !important; vertical-align: top !important; }
        .rehab-table th { background: #f2f2f2 !important; font-weight: bold !important; text-align: center !important; }
        
        .obj-status { font-size: 10px !important; font-weight: bold !important; display: block !important; margin-top: 5px !important; color: #555 !important; }

        .footer-signs { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 50px !important; margin-top: 50px !important; }
        .sig-box { border-top: 1.5px solid #000 !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }

        .legal-notice { margin-top: 30px !important; font-style: italic !important; font-size: 11px !important; text-align: justify !important; }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
        <div className="logo"><img src={logo} alt="Logo" /></div>
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>INDIVIDUALISED REHABILITATION PLAN</h6>
        </div>

        <div className="meta-info">
          <div>
            Client Name:{" "}
            <span className="underline">
              {d.patient
                ? `${d.patient.first_name} ${d.patient.last_name}`
                : "N/A"}
            </span>
          </div>
          <div>Date: <span className="underline">{formatDate(d.created_at)}</span></div>
        </div>

        {/* Domain Checkbox Section */}
        <div className="domain-section">
          <span className="section-label">Target Domain:</span>
          <div className="domain-grid">
            {["Living", "Learning", "Working", "Socializing"].map((domain) => (
              <div key={domain} className="checkbox-item">
                <div className={`box ${d.domain === domain ? "ticked" : ""}`}>{d.domain === domain ? "✓" : ""}</div>
                {domain}
              </div>
            ))}
          </div>
        </div>

        {/* Strengths Section */}
        <div className="list-section">
          <span className="section-label">Strengths (Skills & Resources Possessed):</span>
          <div className="text-block">
            {d.strengths && d.strengths.length > 0 ? d.strengths.map((s, idx) => (
              <div key={idx} style={{ marginBottom: '4px' }}>
                • {s.value}
              </div>
            )) : "No strengths recorded."}
          </div>
        </div>

        {/* Weaknesses Section */}
        <div className="list-section">
          <span className="section-label">Skills and Resources Needed:</span>
          <div className="text-block">
            {d.weaknesses && d.weaknesses.length > 0 ? d.weaknesses.map((w, idx) => (
              <div key={idx} style={{ marginBottom: '4px' }}>
                • {w.value}
              </div>
            )) : "No weaknesses recorded."}
          </div>
        </div>

        {/* Objectives Table */}
        <table className="rehab-table">
          <thead>
            <tr>
              <th style={{ width: "20%" }}>Objectives</th>
              <th style={{ width: "30%" }}>Action Steps</th>
              <th style={{ width: "12%" }}>Location</th>
              <th style={{ width: "12%" }}>Freq/Dur</th>
              <th style={{ width: "13%" }}>Target Date</th>
              <th style={{ width: "13%" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {d.objectives && d.objectives.length > 0 ? d.objectives.map((obj) => {
              const actions = obj.actions || [];
              const rowSpan = actions.length || 1;
              return actions.length > 0 ? (
                actions.map((action, i) => (
                  <tr key={`${obj.id}-${action.id}`}>
                    {i === 0 && (
                      <td rowSpan={rowSpan} style={{ fontWeight: 'bold' }}>
                        {obj.name}
                        <div className="obj-status">Type: {obj.status}</div>
                      </td>
                    )}
                    <td>{action.action_name}</td>
                    <td>{action.location}</td>
                    <td>{action.frequency} / {action.duration}</td>
                    {i === 0 && (
                      <>
                        <td rowSpan={rowSpan} style={{ textAlign: 'center' }}>{formatDate(obj.obj_date)}</td>
                        <td rowSpan={rowSpan} style={{ textAlign: 'center', fontWeight: 'bold' }}>{obj.obj_status}</td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr key={obj.id}>
                  <td style={{ fontWeight: 'bold' }}>{obj.name}</td>
                  <td colSpan={3} className="text-center italic">No actions added</td>
                  <td style={{ textAlign: 'center' }}>{formatDate(obj.obj_date)}</td>
                  <td style={{ textAlign: 'center' }}>{obj.obj_status}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>No rehabilitation objectives recorded.</td></tr>
            )}
          </tbody>
        </table>

        <p className="legal-notice">
          <strong>Agreement:</strong> I have participated in the development of this Individual Rehabilitation Plan and am in agreement with its contents. I understand that this plan will be reviewed periodically to ensure progress towards my goals.
        </p>

        <div className="footer-signs">
          <div>
            <div style={{ height: '40px' }}></div>
            <div className="sig-box">Client's Signature & Date</div>
          </div>
          <div>
            <div style={{ height: '40px' }}></div>
            <div className="sig-box">Psych Rehab Practitioner's Signature & Date</div>
          </div>
        </div>

        <div style={{ marginTop: '40px', borderTop: '1px solid #000', paddingTop: '10px', textAlign: 'center', fontSize: '10px' }}>
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph : 021-34546364-65-66</p>
          <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}